import { SCHEMA_VERSION, type TableRows } from '@core/db';

/**
 * Формат архива дел — [D-55](docs/DECISIONS.md), шаг 19 плана.
 *
 * ```
 * manifest.json                       что это за архив и чем снят
 * db.json                             содержимое базы построчно
 * files/cases/<дело>/<документ>.pdf   сами файлы, теми же путями, что на устройстве
 * ```
 *
 * **Почему `db.json`, а не сам файл базы.** SQLite у нас лежит внутри OPFS или внутри
 * IndexedDB — в зависимости от версии iOS ([D-50](docs/DECISIONS.md)), — и добраться
 * до его байтов снаружи нельзя: имена файлов в пуле OPFS служебные, а в IndexedDB базы
 * как файла нет вовсе. Архив с одного телефона обязан открываться на другом, где ветка
 * может быть другой. Строки такой зависимости не имеют — и заодно читаются глазами,
 * когда архив приходится разбирать руками.
 */

/** Версия самого формата архива. Растёт, когда меняется раскладка файлов внутри. */
export const FORMAT_VERSION = 1;

export const MANIFEST_PATH = 'manifest.json';
export const DATABASE_PATH = 'db.json';
export const FILES_PREFIX = 'files/';

/** Сколько чего в архиве — показывается юристу **до** загрузки. */
export interface ArchiveContents {
  cases: number;
  documents: number;
  clients: number;
  events: number;
  files: number;
}

export interface ArchiveManifest {
  app: 'yatwa';
  kind: 'archive';
  formatVersion: number;
  /** Версия схемы базы. Совпадает с версией Room — `schema.ts`. */
  schemaVersion: number;
  createdAt: number;
  appVersion: string;
  contents: ArchiveContents;
}

/** Разобранный архив: описание, содержимое базы и файлы по путям устройства. */
export interface ParsedArchive {
  manifest: ArchiveManifest;
  tables: TableRows;
  files: Map<string, Blob>;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const count = (value: unknown): number => (typeof value === 'number' && value >= 0 ? value : 0);

/**
 * Проверяет описание архива и приводит его к нашему виду.
 *
 * Проверка не формальность: юрист выбирает файл системным окном, и туда одинаково легко
 * попадают чужой ZIP, архив от другого приложения и архив от версии, которой это
 * приложение ещё не умеет. Каждый случай должен кончаться внятной фразой, а не разбором
 * чужих данных до первой ошибки.
 */
export function readManifest(value: unknown): ArchiveManifest {
  if (!isObject(value) || value['app'] !== 'yatwa' || value['kind'] !== 'archive') {
    throw new Error('Это не архив Yatwa');
  }

  const formatVersion = count(value['formatVersion']);
  if (formatVersion > FORMAT_VERSION) {
    throw new Error('Архив снят более новой версией приложения — обновите его');
  }

  const schemaVersion = count(value['schemaVersion']);
  if (schemaVersion > SCHEMA_VERSION) {
    throw new Error('Архив снят более новой версией приложения — обновите его');
  }

  /*
   * Архив старой схемы прогонялся бы через миграции — их место здесь.
   *
   * Пока их нет, и это не упущение: схема существует в единственной версии 5,
   * общей с Room ([D-44](docs/DECISIONS.md)). Первая же смена версии добавит сюда
   * прогон, а до тех пор честнее отказать, чем сделать вид, что данные разобраны.
   */
  if (schemaVersion < SCHEMA_VERSION) {
    throw new Error(`Архив старой версии (${schemaVersion}) — эта сборка его не читает`);
  }

  const contents = isObject(value['contents']) ? value['contents'] : {};

  return {
    app: 'yatwa',
    kind: 'archive',
    formatVersion,
    schemaVersion,
    createdAt: count(value['createdAt']),
    appVersion: typeof value['appVersion'] === 'string' ? value['appVersion'] : '?',
    contents: {
      cases: count(contents['cases']),
      documents: count(contents['documents']),
      clients: count(contents['clients']),
      events: count(contents['events']),
      files: count(contents['files']),
    },
  };
}

/** Имя файла архива: дата первой, чтобы копии сортировались по порядку. */
export function archiveFileName(at: number): string {
  const moment = new Date(at);
  const pad = (value: number) => String(value).padStart(2, '0');

  return `yatwa-${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}.zip`;
}
